import { Controller } from 'stimulus'

import License from '../classes/license'
import SnackBar from '../../_components/snackbar/snackbar'
import { niceDateFromTimestamp, isEmail } from '../base/utils'
import { getCurrencySet } from '../base/plans'

export default class LicenseOperationController extends Controller {
  static MIN_SEATS = 1

  static targets = [
    'renewUpdatesUntil', // Renew: updates until date
    'renewTotalCost', // Renew: total cost
    'addSeatsMonthsLeft', // Add seats: months left on license
    'addSeatsTotalCost', // Add seats: total cost
    'submit', // CTA button
    'smallCopy', // Small copy paragraph below CTA
    'renewalDate', // Used in the inactive state
    'licenseStatus', // Card overview license status
    'seatsUsed', // Card overview seats used
    'expiryDate', // Card overview expiry date
    'card', // Card wrapper element
    'disabledCard', // Card placeholder for when an operation isn't available
    'enabledCard', // Card for when an operation is available
    'alertFewerSeats', // How many seats below the currently allowed
    'input', // Input field for seats amount
    'licenseKey', // License key hidden input
    'seats', // Seats hidden input
    'operation', // Operation hidden input
    'licenseID', // License ID hidden input
    'buyer', // Buyer hidden input
    'checkbox', // Buy on behalf of someone else checkbox
    'email', // Email if someone is buying on behalf of the customer
    'currency', // Input set in the "_stripe_checkout-form.liquid to formulate te '/store'"
    'currencyVatCopy', // Span element on the "_license-operation.liquid to show the 'prices on ...' copy"
  ]

  static classes = ['fewerSeats', 'disabled']

  static values = {
    type: String,
  }

  licenseInst = null

  seatCount = null

  errorSnackbar = null

  connect() {
    this.disable()
  }

  validate() {
    this.submitTarget.disabled = this.checkbox.checked && !isEmail(this.email.value)

    if (this.checkbox.checked) {
      this.email.style = ''
    } else {
      this.email.style = 'visibility: hidden;'
    }
  }

  onInputChange(event) {
    const value = parseInt(event.target.value, 10) || 1

    this.updateCardValues(value)
    this.toggleFewerSeatsAlert(value)
    this.hideError()
  }

  onIncrease(_event) {
    const value = parseInt(this.inputTarget.value, 10)
    const newValue = value < LicenseOperationController.MIN_SEATS ? LicenseOperationController.MIN_SEATS : value + 1

    this.updateCardValues(newValue)
    this.toggleFewerSeatsAlert(newValue)
    this.hideError()
  }

  onDecrease(_event) {
    const value = parseInt(this.inputTarget.value, 10)
    const newValue = value > LicenseOperationController.MIN_SEATS ? value - 1 : LicenseOperationController.MIN_SEATS

    this.updateCardValues(newValue)
    this.toggleFewerSeatsAlert(newValue)
    this.hideError()
  }

  toggleFewerSeatsAlert(currentSeats) {
    if (this.typeValue === 'addSeats') {
      return
    }

    const isLessThanCurrent = this.licenseInst.maxSeats > currentSeats
    this.alertFewerSeatsTarget.textContent = this.licenseInst.maxSeats - currentSeats

    this.element.classList.toggle(this.fewerSeatsClass, isLessThanCurrent)
  }

  async onSubmit(event) {
    // Prevent submit event from firing right away
    event.preventDefault()

    const newSeats = this.inputTarget.value

    if (!newSeats || parseInt(newSeats, 10) < LicenseOperationController.MIN_SEATS) {
      this.displayZeroSeatsError()
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const licenseID = urlParams.get('license_id')

    if (licenseID !== null) {
      this.licenseKeyTarget.remove()
      this.licenseIDTarget.value = licenseID
    } else {
      this.licenseIDTarget.remove()
      this.licenseKeyTarget.value = this.licenseInst.key
    }

    this.seatsTarget.value = this.inputTarget.value
    this.operationTarget.value = this.element.dataset.apiEndpoint
    this.currencyTarget.value = this.licenseInst.currency

    if (this.checkbox.checked) {
      this.buyer.value = this.email.value
    }

    // Now that we injected the payment data in the form we can submit it
    event.target.submit()
  }

  updateCardValues(seatsValue) {
    this.seatCount = seatsValue

    if (!this.typeValue || !this.licenseInst) {
      return
    }

    if (this.typeValue === 'renew') {
      this.renewUpdatesUntilTarget.textContent = niceDateFromTimestamp(this.licenseInst.renewalNewExpiryDate)
      this.inputTarget.value = seatsValue
      this.renewTotalCostTarget.textContent = License.annualSeatsCost(
        seatsValue,
        this.licenseInst.renewalSeatPrice,
        this.licenseInst.currency
      )

      this.renewalDateTarget.textContent = this.licenseInst.renewalDateFormatted
    } else if (this.typeValue === 'addSeats') {
      this.addSeatsMonthsLeftTarget.textContent = this.licenseInst.monthsLeft
      this.inputTarget.value = seatsValue
      this.addSeatsTotalCostTarget.textContent = License.seatsCostForTimeLeft(
        seatsValue,
        this.licenseInst.proratedAddSeatPrice,
        this.licenseInst.currency
      )
    }

    const { text } = getCurrencySet(this.licenseInst.currency)
    this.currencyVatCopyTarget.textContent = `Prices in ${text} – `
  }

  disable() {
    this.disabledCardTarget.classList.remove(this.disabledClass)
    this.enabledCardTarget.classList.add(this.disabledClass)
  }

  enable() {
    this.disabledCardTarget.classList.add(this.disabledClass)
    this.enabledCardTarget.classList.remove(this.disabledClass)
  }

  clean() {
    this.disable()
    this.updateCardValues(LicenseOperationController.MIN_SEATS)
  }

  displayZeroSeatsError() {
    this.hideError()

    this.errorSnackbar = new SnackBar('Can’t proceed with 0 seats — please increase.', {
      modifiers: 'error no-max-width',
      position: 'top',
    })
  }

  hideError() {
    if (this.errorSnackbar) {
      this.errorSnackbar.remove()
      this.errorSnackbar = null
    }
  }

  updateCardOverviewValues() {
    const statusString = this.licenseInst.isExpiring ? 'Expiring' : this.licenseInst.stateString
    this.licenseStatusTarget.textContent = statusString
    this.seatsUsedTarget.textContent = `${this.licenseInst.usedSeats} of ${this.licenseInst.maxSeats}`
    this.expiryDateTarget.textContent = this.licenseInst.expirationDateFormatted
  }

  set license(license) {
    if (!license) {
      this.clean()
      return
    }

    let previousSeatValue = null
    if (this.licenseInst && this.licenseInst.key === license.key) {
      previousSeatValue = this.seatCount
    }

    this.licenseInst = license

    const seatsValue =
      previousSeatValue ||
      (this.typeValue === 'renew' ? this.licenseInst.maxSeats : LicenseOperationController.MIN_SEATS)

    this.updateCardValues(seatsValue)
    this.updateCardOverviewValues()

    if (
      this.typeValue === 'renew' &&
      (this.licenseInst.stateId === License.stateIds.EXPIRING || this.licenseInst.stateId === License.stateIds.EXPIRED)
    ) {
      this.enable()
    } else if (
      this.typeValue === 'addSeats' &&
      this.licenseInst.canAddSeats &&
      (this.licenseInst.stateId === License.stateIds.EXPIRING || this.licenseInst.stateId === License.stateIds.ACTIVE)
    ) {
      this.enable()
    }
  }

  get email() {
    return this.emailTarget
  }

  get checkbox() {
    return this.checkboxTarget
  }

  get buyer() {
    return this.buyerTarget
  }
}
